import React, { useState } from 'react';
import { MasterTransaction } from '../types';
import { MessageSquareWarning } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../lib/formatters';

interface Props {
  transactions: MasterTransaction[];
  displayTipBreakdown?: boolean;
  onOpenDispute?: (context: { targetTransactionId?: string; shiftContext?: { date: string; shiftId: string } }) => void;
}

export const TransactionHistory: React.FC<Props> = ({ transactions, displayTipBreakdown = true, onOpenDispute }) => {
  const navigate = useNavigate();

  const sortedTransactions = [...transactions].sort((a, b) => {
    const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp ? new Date(a.timestamp).getTime() : new Date(a.tipDate).getTime());
    const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp ? new Date(b.timestamp).getTime() : new Date(b.tipDate).getTime());
    return timeB - timeA;
  });

  const handleDisputeClick = (txId: string) => {
    if (onOpenDispute) {
      onOpenDispute({ targetTransactionId: txId });
    } else {
      navigate(`/disputes?tab=new_dispute&txId=${txId}`);
    }
  };

  return (
    <div className="card">
        <div className="p-6 border-b border-gray-50 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">Tip History</h3>
              <p className="text-xs text-gray-400 font-medium mt-0.5">Recorded shift earnings and ledger allocations</p>
            </div>
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
              Master Ledger
            </span>
        </div>
        
        {/* Mobile View */}
        <div className="md:hidden divide-y divide-gray-50">
          {sortedTransactions.length > 0 ? sortedTransactions.slice(0, 25).map((tx) => (
            <div key={tx.id} className="p-5 space-y-3">
              <div className="flex justify-between items-start gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-gray-900">{tx.tipDate}</p>
                    {tx.staffName && (
                      <span className="text-xs font-semibold text-gray-700">({tx.staffName})</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-1">
                    <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider ${
                      tx.type === 'Allocated' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/60' : 'bg-amber-50 text-amber-700 border border-amber-200/60'
                    }`}>
                      {tx.type}
                    </span>
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                      {tx.source === 'POS' ? 'Paycheck' : (tx.source || 'Cash')}
                    </span>
                    {tx.posId && (
                      <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[8px] font-mono font-bold uppercase">
                        POS: {tx.posId}
                      </span>
                    )}
                    {tx.staffId && (
                      <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[8px] font-mono font-bold" title={`Staff ID: ${tx.staffId}`}>
                        ID: {tx.staffId.slice(0, 8)}
                      </span>
                    )}
                  </div>
                  <span className="text-[9px] font-mono text-gray-400 select-all block mt-1">Tx: #{tx.id.substring(0, 8)}</span>
                </div>
                
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className={`text-base font-black ${tx.type === 'Allocated' ? 'text-emerald-600' : 'text-gray-900'}`}>
                    {formatCurrency(tx.amount || 0)}
                  </span>
                  
                  <button 
                    type="button"
                    onClick={() => handleDisputeClick(tx.id)}
                    className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 active:bg-rose-200 border border-rose-200/80 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-2xs"
                  >
                    <MessageSquareWarning size={13} className="text-rose-600 shrink-0" />
                    <span>Report Dispute</span>
                  </button>
                </div>
              </div>
            </div>
          )) : (
            <div className="p-10 text-center text-gray-400 font-medium italic">No transactions found</div>
          )}
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-100">
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest text-left">Date & Staff</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest text-left">Type & Source</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest text-right">Amount</th>
                <th className="px-6 py-4 font-bold text-gray-400 text-[10px] uppercase tracking-widest text-right">Report Dispute</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sortedTransactions.slice(0, 25).map((tx) => (
                <tr key={tx.id} className="group hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 font-semibold text-gray-900 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-gray-900">{tx.tipDate}</span>
                        {tx.staffName && (
                          <span className="text-xs font-semibold text-gray-600">({tx.staffName})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] font-mono text-gray-400 select-all">Tx: #{tx.id.substring(0, 8)}</span>
                        {tx.posId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-blue-100 text-blue-800 text-[8px] font-mono font-bold uppercase">
                            POS ID: {tx.posId}
                          </span>
                        )}
                        {tx.staffId && (
                          <span className="inline-flex items-center px-1.5 py-0.2 rounded bg-slate-200 text-slate-700 text-[8px] font-mono font-bold" title={`Full Staff ID: ${tx.staffId}`}>
                            Staff ID: {tx.staffId.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${
                        tx.type === 'Allocated' 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200/60' 
                          : 'bg-amber-50 text-amber-700 border-amber-200/60'
                      }`}>
                        {tx.type}
                      </span>
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                        {tx.source === 'POS' ? 'Paycheck (POS)' : (tx.source || 'Cash')}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <span className={`text-sm font-black ${tx.type === 'Allocated' ? 'text-emerald-600' : 'text-gray-900'}`}>
                      {formatCurrency(tx.amount || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <button 
                      type="button"
                      onClick={() => handleDisputeClick(tx.id)}
                      className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 hover:text-rose-800 active:bg-rose-200 border border-rose-200/90 rounded-xl text-xs font-bold transition-all shadow-2xs hover:shadow-xs active:scale-95"
                      title="Report an issue or dispute regarding this tip transaction"
                    >
                      <MessageSquareWarning size={13} className="text-rose-600 shrink-0" />
                      <span>Report Dispute</span>
                    </button>
                  </td>
                </tr>
              ))}
              {sortedTransactions.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-10 text-center text-gray-400 font-medium italic">
                    No transactions recorded for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
    </div>
  );
};

